import theme from './RemoveProjectMemberDialog.module.scss';
import { LoadingIndicator, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Dialog, SelectionList } from '@ygtechnologies/rubus-ui';
import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useAsyncState, useGetActiveProfile, useGetProjectId, useInterval } from '@/utils/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import {
    changeParticipantUser,
    getChangeRequestAvailableParticipants,
    getChangeRequestParticipants,
    searchChangeRequests,
} from '@/services/change-requests';
import { ChangeRequest, ChangeRequestAvailableParticipant, ProjectMemberDetails } from '@/types';
import ChangeRequestListItem from '@/seller/components/change-requests/ChangeRequestList/ChangeRequestListItem';
import { useMappedChangeRequest } from '@/seller/components/change-requests/ChangeRequestFilterableList';
import { UserIcon } from '@/components/Icons';
import { PromiseCheckList, PromiseCheckListItem, SendProgress } from '@/components/PromiseCheckList';
import { intersection, uniq } from 'lodash-es';

interface ChangeRequestAvailableParticipantsWithId extends ChangeRequestAvailableParticipant {
    id: number;
}

type RemoveProjectMemberModalModalProps = {
    isOpen: boolean;
    toggle: () => void;
    onRemoved: (pm: ProjectMemberDetails) => void;
    projectMember: ProjectMemberDetails;
};

enum Steps {
    Info,
    Review,
    Transfer,
    Confirm,
    Finished,
}
interface QueueItem {
    changeRequest: ChangeRequest;
    status: SendProgress;
}

const MaxActiveJobs = 2;
const RemoveProjectMemberModal: React.FC<RemoveProjectMemberModalModalProps> = ({
    isOpen,
    toggle,
    onRemoved,
    projectMember,
}) => {
    const projectId = useGetProjectId();
    const steps = [Steps.Info, Steps.Review, Steps.Transfer, Steps.Confirm, Steps.Finished];
    const [step, setStep] = useState(Steps.Info);
    const [jobQueue, setJobQueue] = useState<QueueItem[]>([]);
    const [queueStatus, setQueueStatus] = useState<'not-started' | 'in-progress' | 'finished'>('not-started');

    const [participatingChangeRequests] = useAsyncState(
        () => searchChangeRequests({ participants: [projectMember.userId!], projectId }),
        [projectMember, projectId],
        null
    );

    const mappedChangeRequests = useMappedChangeRequest(participatingChangeRequests);
    const currentUser = useGetActiveProfile();

    const [transferToParticipant, setTransferToParticipant] = useState<ChangeRequestAvailableParticipantsWithId | null>(
        null
    );
    // Builds the options for the dropdown
    const [availableParticipants] = useAsyncState(() => getChangeRequestAvailableParticipants(projectId!), [projectId], []);

    // doing this incase there's multiple users with same userId in different companies?
    // Id is 0 from the API
    const fixedParticipants = availableParticipants
        .filter((x) => x.userId !== projectMember.userId)
        .map((x, i) => ({ ...x, id: i }));

    const activeCategories = uniq(
        projectMember.categoryPermissions
            .filter((x) => x.permission.includes('UseInChangeRequests'))
            .map((x) => x.categoryId)
    );

    const filteredParticipants = fixedParticipants.map((x) => ({
        ...x,
        isDisabled: !(
            x.isAdministrator || intersection(x.availableCategories, activeCategories).length === activeCategories.length
        ),
    }));
    // Sets the selected member to transfer to

    function setCurrentStep(num) {
        setStep(num);
    }
    useInterval(() => {
        if (queueStatus === 'in-progress') {
            processQueue();
        }
    }, 50);

    const processQueue = async () => {
        if (!transferToParticipant || queueStatus !== 'in-progress') return;
        const activeJobs = jobQueue.filter((x) => x.status === SendProgress.InProgress).length;
        if (activeJobs >= MaxActiveJobs) return;

        const nextJob = jobQueue.find((x) => x.status === SendProgress.NotStarted);
        if (!nextJob) {
            setQueueStatus('finished');
            onRemoved(projectMember);
            return;
        }

        setJobQueue((prev) =>
            prev.map((x) =>
                x.changeRequest.id === nextJob.changeRequest.id ? { ...x, status: SendProgress.InProgress } : x
            )
        );
        try {
            const participants = await getChangeRequestParticipants(nextJob.changeRequest.id);
            const relevantParticipant = participants.find((x) => x.userId === projectMember.userId);

            if (!relevantParticipant) return;
            // await sleep(2000);

            await changeParticipantUser(
                relevantParticipant.id,
                transferToParticipant.profileId,
                transferToParticipant.userId
            );
            setJobQueue((prev) =>
                prev.map((x) =>
                    x.changeRequest.id === nextJob.changeRequest.id ? { ...x, status: SendProgress.Success } : x
                )
            );
        } catch (e) {
            setJobQueue((prev) =>
                prev.map((x) =>
                    x.changeRequest.id === nextJob.changeRequest.id ? { ...x, status: SendProgress.Failed } : x
                )
            );
        }
    };

    const handleRemove = async () => {
        if (participatingChangeRequests?.length === 0) return;
        setJobQueue(participatingChangeRequests!.map((x) => ({ changeRequest: x, status: SendProgress.NotStarted })));
        setQueueStatus('in-progress');
    };

    const handleUserRemove = () => {
        onRemoved(projectMember);
        toggle();
    };

    const renderBody = () => {
        if (!currentUser) {
            return <LoadingIndicator />;
        }
        if (currentUser?.id === projectMember.userId) {
            return (
                <>
                    <Trans>
                        You cannot remove yourself from the project. Another Admin user has to perform this action.
                    </Trans>
                    <ButtonBar className={theme.buttonBar}>
                        <ButtonBar.Right>
                            <Button onClick={toggle} variant="primary">
                                <Trans>Ok</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </>
            );
        }
        if (participatingChangeRequests && participatingChangeRequests.length > 0 && projectMember.userId) {
            return (
                <SimpleWizard
                    onCancel={toggle}
                    onSubmit={handleRemove}
                    steps={steps}
                    currentStep={step}
                    onStepChange={setCurrentStep}>
                    <SimpleWizardStep
                        stepNumber={Steps.Info}
                        header={<Trans>Information on user</Trans>}
                        nextButton={() => (
                            <Button variant="primary" onClick={() => setStep(step + 1)}>
                                <Trans>Transfer and remove</Trans>
                            </Button>
                        )}>
                        <div className={theme.wizContent}>
                            <div>
                                {participatingChangeRequests.length === 1 ? (
                                    <Trans>
                                        <b>{`${projectMember.displayName} `}</b> is participant in
                                        <b> {participatingChangeRequests.length} </b> change request. Therefore, you must
                                        transfer that to another user before removing this user.
                                    </Trans>
                                ) : (
                                    <Trans>
                                        <b>{`${projectMember.displayName} `}</b> is participant in
                                        <b> {participatingChangeRequests.length} </b> change requests. Therefore, you must
                                        transfer these to another user before removing this user.
                                    </Trans>
                                )}
                            </div>
                            <div className={theme.spacer}></div>
                            {/* <Banner type="info" className={theme.infoBanner}>
                                    <Trans>
                                        Please note, whomever the cases are transferred to will automatically become the
                                        Assignee.
                                    </Trans>
                                </Banner> */}
                        </div>
                    </SimpleWizardStep>

                    <SimpleWizardStep
                        stepNumber={Steps.Review}
                        header={<Trans>Review change request</Trans>}
                        nextButton={() => (
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setJobQueue(
                                        participatingChangeRequests!.map((x) => ({
                                            changeRequest: x,
                                            status: SendProgress.NotStarted,
                                        }))
                                    );
                                    setStep(step + 1);
                                }}>
                                <Trans>Next</Trans>
                            </Button>
                        )}>
                        <div className={theme.wizContent}>
                            <div className={theme.caseListConatiner}>
                                <h3>
                                    <Trans>Change requests</Trans>
                                </h3>
                                <div className={theme.caseList}>
                                    {mappedChangeRequests!.map((cr) => (
                                        <ChangeRequestListItem
                                            key={cr.id}
                                            changeRequest={cr}
                                            onChange={() => {}}
                                            reloadChangeRequests={() => {}}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={theme.spacer}></div>
                    </SimpleWizardStep>

                    <SimpleWizardStep
                        nextButton={() => (
                            <Button
                                disabled={transferToParticipant === null}
                                variant="primary"
                                onClick={() => setStep(step + 1)}>
                                <Trans>Next</Trans>
                            </Button>
                        )}
                        stepNumber={Steps.Transfer}
                        header={<Trans>Select new participant</Trans>}>
                        <div className={theme.wizContent}>
                            <SelectionList selectionMode="single">
                                {filteredParticipants
                                    .filter((x) => x.userId !== projectMember.userId)
                                    .map((ap) => (
                                        <SelectionList.Item
                                            disabled={ap.isDisabled}
                                            key={ap.userId}
                                            selected={transferToParticipant?.id === ap.id}
                                            onChange={() => setTransferToParticipant(ap)}
                                            label={
                                                <div className={theme.itemContent}>
                                                    <UserIcon userId={ap.userId} sizeInPx={36} />
                                                    <span className={theme.name}>{ap.displayName}</span>
                                                    <small>{ap.profileName}</small>
                                                </div>
                                            }
                                        />
                                    ))}
                            </SelectionList>
                            <AlertBanner type="info">
                                <Trans>
                                    <strong>Please note</strong> you can only select a user who has the same categories as
                                    those in the offers, or an administrator.
                                </Trans>
                            </AlertBanner>
                        </div>
                    </SimpleWizardStep>

                    <SimpleWizardStep
                        stepNumber={Steps.Confirm}
                        header={<Trans>Confirm transfer</Trans>}
                        nextButton={() => (
                            <Button
                                variant="danger"
                                onClick={() => {
                                    handleRemove();
                                    setStep(step + 1);
                                }}>
                                <Trans>Yes, transfer and remove user</Trans>
                            </Button>
                        )}>
                        <div className={theme.wizContent}>
                            <div className={theme.overview}>
                                <Trans>Replacement process</Trans>
                                <div className={theme.assignee}>
                                    <UserIcon userId={projectMember.userId} sizeInPx={60} className={theme.thumbnail} />
                                    <div>
                                        <h5>{projectMember.displayName}</h5>
                                    </div>
                                </div>

                                <FontAwesomeIcon icon={faArrowDown} className={theme.faArrow} />

                                <div className={theme.assignee}>
                                    <UserIcon userId={transferToParticipant?.id} sizeInPx={60} className={theme.thumbnail} />
                                    <div>
                                        <h5>{transferToParticipant?.displayName}</h5>
                                        {transferToParticipant?.profileName}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SimpleWizardStep>

                    <SimpleWizardStep stepNumber={Steps.Finished} hideButtons={true}>
                        <h3>
                            <Trans>Transfer status</Trans>
                        </h3>
                        <PromiseCheckList>
                            {jobQueue.map((x) => (
                                <PromiseCheckListItem
                                    key={x.changeRequest.id}
                                    progress={x.status}
                                    text={x.changeRequest.subject}
                                />
                            ))}
                        </PromiseCheckList>

                        {queueStatus === 'finished' && (
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className={theme.faSuccess} />
                                <div className={theme.completeMessage}>
                                    <Trans>
                                        All change requests transferred to {transferToParticipant?.displayName} and{' '}
                                        {projectMember.displayName} is removed from project.
                                    </Trans>
                                </div>
                                <div className={theme.footer}>
                                    <Button variant="primary" onClick={toggle}>
                                        <Trans>Close</Trans>
                                    </Button>
                                </div>
                            </>
                        )}
                    </SimpleWizardStep>
                </SimpleWizard>
            );
        }
        return (
            <>
                <Trans>Are you sure you would like to remove{` ${projectMember.displayName}?`}</Trans>
                <ButtonBar className={theme.buttonBar}>
                    <Button onClick={toggle} variant="tertiary">
                        <Trans>Cancel</Trans>
                    </Button>

                    <ButtonBar.Right>
                        <Button onClick={handleUserRemove} variant="danger">
                            <Trans>Remove user</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()}>
            <Dialog.Content contentWidth="auto" className={theme.modal}>
                <Dialog.Header>
                    <Trans>Remove user</Trans>
                </Dialog.Header>
                {renderBody()}
            </Dialog.Content>
        </Dialog>
    );
};

export default RemoveProjectMemberModal;
